import { inject, Injectable } from '@angular/core';
import { AlertService } from '@core/services/alert.service';
import {
  BasketSummaryResponseDto,
  RebookingSelectedSeatResponseDto,
  ResubscriptionCategoryPriceResponseDto,
  SubscriptionDetailResponseDto,
} from '@core/services/apiclient';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import * as fromSales from '@root-store/sales-store/index';
import * as fromSalesPlan from '@root-store/sales-plan-store/index';
import { ResubSeatsValidationResult, SalesEventTypeEnum } from '@sales/models/sales.model';
import { BasketService } from '@sales/services/basket.service';
import { SalesFactoryService } from '@sales/services/sales-factory.service';
import { SalesResubscriptionService } from '@sales/services/resubscription/sales-resubscription.service';
import { SalesUtilsService } from '@sales/services/sales-utils.service';
import { of } from 'rxjs';
import { catchError, concatMap, filter, map, mergeMap, switchMap, tap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class SalesEffects {
  private readonly actions = inject(Actions);
  private readonly store = inject<Store<fromSales.State>>(Store);
  private readonly basketService = inject(BasketService);
  private readonly alertService = inject(AlertService);
  private readonly salesService = inject(SalesFactoryService);

  private readonly salesUtils = inject(SalesUtilsService);
  private readonly resubService = inject(SalesResubscriptionService);

  cancelBasket$ = createEffect(() => {
    return this.actions.pipe(
      ofType(fromSales.actions.cancelSalesBasket),
      withLatestFrom(this.store.pipe(select(fromSales.selectors.selectChannel))),
      switchMap(([{ withSetChannel, showToast }, channelId]) => {
        if (channelId) {
          return this.basketService.cancelBasket().pipe(
            concatMap((result) => {
              if (result) {
                if (withSetChannel) {
                  if (showToast) {
                    this.alertService.showCustom('sales.info.saleCanceled', 'success');
                  }
                  return [fromSales.actions.setChannel(null)];
                }
                return [{ type: 'Empty Action' }];
              } else {
                // Le backend renvoie false sans ProblemDetails (pas de traceId possible).
                // TODO: basculer sur showApiError quand cancelBasket sera migré UseCase.
                this.alertService.showCustom('sales.errors.problemWhenCancelingSale', 'danger');
                return [{ type: 'Empty Action' }];
              }
            }),
            catchError((err: unknown) => {
              this.alertService.showApiError(err, 'sales.errors.problemWhenCancelingSale');
              return of(fromSales.actions.setChannel(null));
            }),
          );
        }
        return [{ type: 'Empty Action' }];
      }),
    );
  });

  loadSubscriptionDetails$ = createEffect(() => {
    return this.actions.pipe(
      ofType(fromSales.actions.loadSubscriptionDetails),
      switchMap(({ subscriptionId }) =>
        this.salesService.getSubscriptionDetails(subscriptionId).pipe(
          concatMap((details: SubscriptionDetailResponseDto) => [
            fromSales.actions.setSubscriptionDetails(details),
          ]),
          catchError(() => of(null)),
        ),
      ),
    );
  });

  loadBasketInfo$ = createEffect(() => {
    return this.actions.pipe(
      ofType(fromSales.actions.loadBasketInfo),
      withLatestFrom(this.store.pipe(select(fromSales.selectors.selectBasketId))),
      switchMap(([action, basketId]) =>
        this.basketService.getBasketSummary(basketId).pipe(
          concatMap((data: BasketSummaryResponseDto) => {
            const transformedData = this.salesUtils.groupBasketItems(data);
            return [
              fromSales.actions.updateBasketInfoSuccess({ summary: transformedData }),
              fromSales.actions.updateBasketCompleted({ source: action.source, context: action.context }),
            ];
          }),
          catchError(() => of(null)),
        ),
      ),
    );
  });

  afterUnbookAndBasketUpdate = createEffect(() => {
    return this.actions.pipe(
      ofType(fromSales.actions.updateBasketCompleted),
      filter((action) => action.source === 'unbook'),
      mergeMap((action) => {
        const context = action.context;
        if (context?.eventType === SalesEventTypeEnum.Resubscription) {
          return [fromSales.actions.removeRebooking({ basketSubscriptionIds: context.idsToUnbook })];
        } else {
          return [fromSales.actions.removeBooking(context.bookingId, context.session?.id)];
        }
      }),
    );
  });

  validateResubSeatSelection$ = createEffect(() => {
    return this.actions.pipe(
      ofType(fromSales.actions.validateResubSeatSelection),
      withLatestFrom(this.store.pipe(select(fromSales.selectors.selectChannel))),
      switchMap(([{ seats, subscriptionId }, channelId]) =>
        this.resubService.getRebookingSelectedSeats(seats).pipe(
          switchMap((result: RebookingSelectedSeatResponseDto[]) => {
            if (!result) return of(null);
            const categoryIds = this.extractCategoryIds(result);

            return this.resubService.getResusbcriptionPrices(subscriptionId, channelId, categoryIds).pipe(
              map((pricesMap: ResubscriptionCategoryPriceResponseDto[]) =>
                this.mapValidationResult(result, pricesMap),
              ),
              concatMap((resubSeatsResult: ResubSeatsValidationResult[]) => {
                return [
                  fromSales.actions.validateResubSeatSelectionSuccess({
                    resubSeatsResult: resubSeatsResult,
                  }),
                  fromSalesPlan.actions.resetPlan(),
                ];
              }),
            );
          }),
          catchError(() => of(null)),
        ),
      ),
    );
  });

  private extractCategoryIds(result: RebookingSelectedSeatResponseDto[]): number[] {
    return [...new Set(result.map((r) => r?.category?.id).filter(Boolean))];
  }

  private mapValidationResult(
    result: RebookingSelectedSeatResponseDto[],
    pricesMap: ResubscriptionCategoryPriceResponseDto[],
  ): ResubSeatsValidationResult[] {
    return result.map((r) => {
      const categoryPrices = pricesMap.find((p) => p?.categoryId === r?.category?.id);
      return {
        seat: r.seat,
        category: r.category,
        prices: categoryPrices?.prices || [],
      };
    });
  }

  // Load events on channel selection
  loadEventsOnChannel$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromSales.actions.setChannel),
      filter(({ channelId }) => !!channelId),
      switchMap(({ channelId }) =>
        this.salesService.salesEventsService.getEventSessions(channelId).pipe(
          map((events) => fromSales.actions.loadEventsSuccess({ events })),
          catchError(() => of(fromSales.actions.loadEventsFailure()))
        )
      )
    )
  );

  // Load subscriptions on channel selection
  loadSubscriptionsOnChannel$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromSales.actions.setChannel),
      filter(({ channelId }) => !!channelId),
      switchMap(({ channelId }) =>
        this.salesService.salesSubscriptionService.getEventSessions(channelId).pipe(
          map((subscriptions) => fromSales.actions.loadSubscriptionsSuccess({ subscriptions })),
          catchError(() => of(fromSales.actions.loadSubscriptionsFailure()))
        )
      )
    )
  );

  // Retry events (manual trigger)
  loadEvents$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromSales.actions.loadEvents),
      switchMap(({ channelId }) =>
        this.salesService.salesEventsService.getEventSessions(channelId).pipe(
          map((events) => fromSales.actions.loadEventsSuccess({ events })),
          catchError(() => of(fromSales.actions.loadEventsFailure()))
        )
      )
    )
  );

  // Retry subscriptions (manual trigger)
  loadSubscriptions$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromSales.actions.loadSubscriptions),
      switchMap(({ channelId }) =>
        this.salesService.salesSubscriptionService.getEventSessions(channelId).pipe(
          map((subscriptions) => fromSales.actions.loadSubscriptionsSuccess({ subscriptions })),
          catchError(() => of(fromSales.actions.loadSubscriptionsFailure()))
        )
      )
    )
  );
}
